import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Carga los archivos de traducción JSON desde `assets/i18n/{lang}.json`.
 *
 * La ruta es relativa (sin `/` inicial) para que se resuelva contra el `<base href>`
 * del index.html — funciona tanto en dev (`/`) como en prod (`/Partnercore/`).
 */
@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string) {
    return forkJoin([
      this.http.get<Translation>(`assets/i18n/${lang}.json`),
      // Traducciones del negocio Conecta (archivo propio → sin conflictos con el upstream PartnerCore).
      this.http.get<Translation>(`assets/i18n/conecta/${lang}.json`).pipe(catchError(() => of({} as Translation))),
    ]).pipe(map(([base, conecta]) => mergeMissing(base, conecta)));
  }
}

/** Agrega a `base` solo las claves que no tiene (recursivo). PartnerCore siempre gana. */
function mergeMissing(base: Translation, extra: Translation): Translation {
  const out: Translation = { ...base };
  for (const [key, value] of Object.entries(extra)) {
    const current = out[key];
    if (isPlainObject(current) && isPlainObject(value)) out[key] = mergeMissing(current, value);
    else if (current === undefined) out[key] = value;
  }
  return out;
}

function isPlainObject(v: unknown): v is Translation {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
